/**
 * TruncationBanner — Sprint A5.3
 *
 * Shows a dismissible amber upsell banner when the shadow crawler indicates
 * the tier page-cap was hit before the full site was covered.
 *
 * Surfaces:
 *   - ProgressTabDesktop (nowAudit)
 *   - ProgressTabMobile  (nowAudit)
 *
 * Returns null when:
 *   - truncationBlock is null (pre-A5.3 audits that have no shadow data)
 *   - state === "complete" (whole site covered)
 *   - user has already dismissed for this domain+state combo
 */

import { useState } from 'react';
import { api } from '../../api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TruncationBlock {
  state: string;          // "complete" | "potentially_truncated" | "definitely_truncated"
  pagesCrawled: number;
  pagesDiscovered: number | null;
  siteConfidence: string | null;  // "high" | "medium" | "low"
}

interface Props {
  truncationBlock: TruncationBlock | null;
  customerTier: string;  // "free" | "starter" | "growth" | "scale"
  /** Stable key for localStorage dismiss (domainId is ideal) */
  dismissKey: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIER_CAP: Record<string, number> = {
  free: 50, tier_1: 50, tier_2: 100, tier_3: 250,
};

const NEXT_TIER: Record<string, { tier: 'tier_1' | 'tier_2' | 'tier_3'; label: string; cap: number } | null> = {
  free:   { tier: 'tier_1', label: 'Starter', cap: 50  },
  tier_1: { tier: 'tier_2', label: 'Growth',  cap: 100 },
  tier_2: { tier: 'tier_3', label: 'Scale',   cap: 250 },
  tier_3: null,
};

function dismissStorageKey(dismissKey: string, state: string): string {
  return `truncationBannerDismissed-${dismissKey}-${state}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TruncationBanner({ truncationBlock, customerTier, dismissKey }: Props) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!truncationBlock || truncationBlock.state === 'complete') return false;
    try {
      return localStorage.getItem(dismissStorageKey(dismissKey, truncationBlock.state)) === '1';
    } catch {
      return false;
    }
  });

  if (
    !truncationBlock ||
    truncationBlock.state === 'complete' ||
    dismissed
  ) {
    return null;
  }

  const { state, pagesCrawled, pagesDiscovered, siteConfidence } = truncationBlock;
  const tierCap = TIER_CAP[customerTier] ?? 50;
  const nextTier = NEXT_TIER[customerTier] ?? null;

  function handleDismiss() {
    try {
      localStorage.setItem(dismissStorageKey(dismissKey, state), '1');
    } catch { /* ignore */ }
    setDismissed(true);
  }

  // ── Build body copy ──────────────────────────────────────────────────────

  // Confidence-aware site-size description
  let siteDescription: string;
  if (pagesDiscovered !== null && pagesDiscovered > 0) {
    const prefix =
      siteConfidence === 'high'   ? 'approximately' :
      siteConfidence === 'medium' ? 'an estimated' :
      'at least';
    siteDescription = `${prefix} ${pagesDiscovered.toLocaleString()} pages`;
  } else {
    siteDescription = 'more pages than your plan covers';
  }

  const isDefinite = state === 'definitely_truncated';

  let title: string;
  let body: string;

  if (nextTier === null) {
    // Scale / highest tier hit cap
    title = `We audited ${pagesCrawled.toLocaleString()} of your pages`;
    body =
      `Your site has ${siteDescription}. The Scale plan covers up to ${tierCap.toLocaleString()} pages — ` +
      `the audit reflects those pages only. Contact support if you need full-site coverage.`;
  } else if (isDefinite) {
    title = `We audited ${pagesCrawled.toLocaleString()} of your ~${
      pagesDiscovered != null ? pagesDiscovered.toLocaleString() : '?'
    } pages`;
    body =
      `Your ${tierCap.toLocaleString()}-page cap was reached before the full site was covered. ` +
      `Upgrade to ${nextTier.label} to audit up to ${nextTier.cap.toLocaleString()} pages and get more accurate scores.`;
  } else {
    // potentially_truncated
    title = `We audited ${pagesCrawled.toLocaleString()} pages`;
    body =
      `Your audit may have been capped — your site could have ${siteDescription}. ` +
      `Upgrade to ${nextTier.label} for deeper coverage and more accurate scoring.`;
  }

  return (
    <div
      className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm mb-4"
      role="note"
      aria-label="Audit coverage notice"
    >
      {/* Icon */}
      <span className="mt-0.5 shrink-0 text-amber-500" aria-hidden="true">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-5 w-5"
        >
          <path
            fillRule="evenodd"
            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
          />
        </svg>
      </span>

      {/* Content */}
      <div className="flex-1">
        <p className="font-medium text-amber-800">{title}</p>
        <p className="mt-0.5 text-amber-700">{body}</p>

        {/* Upgrade CTA — only when there's a next tier */}
        {nextTier !== null && (
          <button
            type="button"
            onClick={async () => {
              const { url } = await api.billing.checkout(nextTier.tier);
              window.location.href = url;
            }}
            className="mt-2 inline-flex items-center gap-1 text-amber-800 underline underline-offset-2 hover:text-amber-900 font-medium"
          >
            Upgrade to {nextTier.label} →
          </button>
        )}
      </div>

      {/* Dismiss */}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss coverage notice"
        className="shrink-0 text-amber-400 hover:text-amber-600 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </button>
    </div>
  );
}
